import React from 'react';
import { Button, CircularProgress, TextField } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faBuilding, faChartBar, faLink, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage, useIntl } from 'react-intl';

import { TranslationKeys } from '../../../../translations';
import { useConnectionInspectorPage } from './useConnectionInspectorPage';
import { COLOR_CONNECTION, COLOR_EMPLOYER, COLOR_OWNER, COLOR_STATS } from './constants';
import { InfoCard } from './components/InfoCard';
import { ConnectionFields, EmployerFields, OwnerFields, StatsFields } from './components/CardFields';

import {
  PageWrapperStyled,
  SearchBarStyled,
  ResultsGridStyled,
  ColumnPairStyled,
} from './ConnectionInspectorPage.styles';

// ─── Page ─────────────────────────────────────────────────────────────────────

export const ConnectionInspectorPage = () => {
  const intl = useIntl();
  const {
    inputValue,
    data,
    isFetching,
    connectionState,
    dependentState,
    handleInputChange,
    handleSearch,
    handleKeyDown,
  } = useConnectionInspectorPage();

  return (
    <PageWrapperStyled>
      {/* Compact search bar */}
      <SearchBarStyled elevation={0}>
        <FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: '#bbb', fontSize: '0.85rem', flexShrink: 0 }} />
        <TextField
          size="small"
          placeholder={intl.formatMessage({ id: TranslationKeys.tools_connectionInspector_searchPlaceholder })}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          sx={{ width: 300 }}
          inputProps={{ 'aria-label': 'Connection ID' }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleSearch}
          disabled={!inputValue.trim() || isFetching}
          startIcon={isFetching ? <CircularProgress size={13} color="inherit" /> : undefined}
          sx={{ flexShrink: 0 }}
        >
          <FormattedMessage id={TranslationKeys.tools_connectionInspector_searchButton} />
        </Button>
      </SearchBarStyled>

      {/* Always-visible result cards: Connection | Employer+Owner | Stats */}
      <ResultsGridStyled>
        {/* Column 1 — Connection */}
        <InfoCard
          icon={faLink}
          color={COLOR_CONNECTION}
          titleId={TranslationKeys.tools_connectionInspector_connectionBox}
          placeholderTextId={TranslationKeys.tools_connectionInspector_connectionData}
          state={connectionState}
        >
          {data && <ConnectionFields data={data} />}
        </InfoCard>

        {/* Column 2 — Employer stacked above Owner */}
        <ColumnPairStyled>
          <InfoCard
            icon={faBriefcase}
            color={COLOR_EMPLOYER}
            titleId={TranslationKeys.tools_connectionInspector_employerBox}
            placeholderTextId={TranslationKeys.tools_connectionInspector_employerData}
            state={dependentState}
          >
            {data && <EmployerFields data={data} />}
          </InfoCard>

          <InfoCard
            icon={faBuilding}
            color={COLOR_OWNER}
            titleId={TranslationKeys.tools_connectionInspector_ownerBox}
            placeholderTextId={TranslationKeys.tools_connectionInspector_ownerData}
            state={dependentState}
          >
            {data && <OwnerFields data={data} />}
          </InfoCard>
        </ColumnPairStyled>

        {/* Column 3 — Stats */}
        <InfoCard
          icon={faChartBar}
          color={COLOR_STATS}
          titleId={TranslationKeys.tools_connectionInspector_statsBox}
          placeholderTextId={TranslationKeys.tools_connectionInspector_statsData}
          state={dependentState}
        >
          {data && <StatsFields data={data} />}
        </InfoCard>
      </ResultsGridStyled>
    </PageWrapperStyled>
  );
};
